import { Fragment, useEffect, useState } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { KeenIcon } from '@/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useGetEnvironmentVariableByIdQuery,
  useUpdateEnvironmentVariableMutation,
} from '@/services/EnvironmentVariablesService';
const EnvironmentVariableForm = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [value, setValue] = useState('');

  const { data: envVar, isLoading: isFetching } =
    useGetEnvironmentVariableByIdQuery(id || '', {
      skip: !id,
    });

  const [updateEnvironmentVariable, { isLoading: isUpdating }] =
    useUpdateEnvironmentVariableMutation();

  useEffect(() => {
    // Do not prefill the value to avoid exposing secrets; always start empty
    setValue('');
  }, [envVar?.id]);

  const handleCancel = () => {
    navigate('/configuration/environment-variables');
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (!value.trim()) {
      enqueueSnackbar('Please enter a value.', { variant: 'error' });
      return;
    }

    try {
      // Backend encrypts at rest; send plaintext only.
      await updateEnvironmentVariable({
        id,
        data: { secret_key: value },
      }).unwrap();
      enqueueSnackbar('Environment variable updated successfully', {
        variant: 'success',
      });
      navigate('/configuration/environment-variables');
    } catch {
      enqueueSnackbar('Failed to update environment variable', {
        variant: 'error',
      });
    }
  };

  if (isFetching) {
    return (
      <div className="container-fixed">
        <div className="text-center py-4" role="status" aria-live="polite">
          Loading environment variable...
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Edit Environment Variable" />
              <ToolbarDescription>
                Update the value of an environment variable. The existing value
                is never shown for security reasons.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-light flex items-center gap-2 h-10"
                onClick={handleCancel}
              >
                <KeenIcon icon="arrow-left" />
                Back to List
              </button>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed flex flex-col gap-4">
        <div>
          <h2 className="text-base font-semibold">
            Edit {envVar?.service_name ?? 'Environment Variable'}
          </h2>
        </div>
        <div>
          <div className="space-y-6">
            <div className="flex flex-col gap-2 max-w-xl">
              <label
                htmlFor="env-var-key"
                className="form-label mb-1 font-medium text-gray-700"
              >
                Name
              </label>
              <input
                id="env-var-key"
                type="text"
                className="input input-bordered w-full bg-gray-100"
                value={envVar?.service_name ?? ''}
                readOnly
                aria-readonly="true"
              />
            </div>

            <div className="flex flex-col gap-2 max-w-xl">
              <label
                htmlFor="env-var-value"
                className="form-label mb-1 font-medium text-gray-700"
              >
                New Value
              </label>
              <input
                id="env-var-value"
                type="password"
                className="input input-bordered w-full"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-describedby="env-var-value-help"
                autoComplete="new-password"
              />
              <p id="env-var-value-help" className="text-xs text-gray-500">
                Existing value is hidden for security. Enter a new value to
                update this environment variable.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                type="button"
                className="btn btn-light"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isUpdating || !value.trim()}
              >
                {isUpdating ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Updating...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <KeenIcon icon="check" className="mr-2" />
                    Update Variable
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export { EnvironmentVariableForm };
